import React, { useState } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { LogOut, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { getMobileRouteChrome } from 'components/navigation/mobileRouteChrome';
import CompanySelector from 'components/sidebar/components/CompanySelector';
import {
  getRelyNavigationIcon,
  getRelyNavigationIconName,
} from 'components/navigation/relyNavigationIcons';
import { ROUTES } from 'constants/routes';
import { useCompany } from 'contexts/CompanyContext';
import { cn } from 'lib/utils';
import { getClientNavigationItems } from 'navigation/clientRouteManifest';
import { hasRelyCommandAccess } from 'components/billing/relyCommandAccess';
import { isRelyCommandPreviewEnabled } from 'config/relyCommandPreview';
import { useBilling } from 'hooks/useBilling';

function getInitial(value) {
  return value?.charAt(0)?.toUpperCase() || 'U';
}

function MobileTab({
  icon: Icon,
  iconKey,
  iconName,
  label,
  to,
  active,
  disabled,
  onClick,
}) {
  const state = disabled ? 'disabled' : active ? 'active' : 'inactive';
  const usesCommandOrbit = iconKey === 'command';
  const content = (
    <>
      {active && !disabled ? (
        <span
          data-testid="mobile-nav-active-indicator"
          data-position="top-rail"
          className="absolute top-0.5 h-0.5 w-5 rounded-full bg-primary-500"
          aria-hidden="true"
        />
      ) : null}
      <span
        data-nav-icon={iconKey}
        data-icon-name={usesCommandOrbit ? 'command-orbit' : iconName}
        data-slot="nav-icon-shell"
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-[10px]',
          'transition-[background-color,color] duration-200',
          active
            ? 'bg-transparent text-primary-700 dark:text-primary-300'
            : 'text-[var(--rely-text-muted)]'
        )}
      >
        {usesCommandOrbit ? (
          <img
            src="/assets/rely-command-orbit.webp"
            alt=""
            width="20"
            height="20"
            draggable="false"
            className="h-5 w-5 object-contain"
            aria-hidden="true"
          />
        ) : (
          <Icon className="h-4 w-4" strokeWidth={2.35} aria-hidden="true" />
        )}
      </span>
      <span
        className={cn(
          'mt-0.5 max-w-[3.75rem] truncate text-[11px] font-semibold leading-none',
          active
            ? 'text-[var(--rely-text-strong)]'
            : 'text-[var(--rely-text-muted)]'
        )}
      >
        {label}
      </span>
    </>
  );

  const className = cn(
    'relative flex min-h-[44px] min-w-0 flex-1 touch-manipulation flex-col items-center justify-center rounded-[14px] bg-transparent px-0.5',
    'transition-[background-color,color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    active && !disabled
      ? 'text-[var(--rely-text-strong)]'
      : 'text-[var(--rely-text-muted)]',
    disabled
      ? 'cursor-not-allowed opacity-40'
      : active
      ? 'active:scale-[0.98]'
      : 'active:scale-[0.97] hover:bg-[var(--rely-surface-elevated)]'
  );

  if (to && !disabled) {
    return (
      <Link
        to={to}
        className={className}
        data-density="compact"
        data-nav-item={iconKey}
        data-state={state}
        data-selection={active && !disabled ? 'rail' : 'none'}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      data-density="compact"
      data-nav-item={iconKey}
      data-state={state}
      data-selection={active && !disabled ? 'rail' : 'none'}
      className={className}
    >
      {content}
    </button>
  );
}

function SheetLink({
  icon: Icon,
  iconKey,
  iconName,
  label,
  to,
  disabled,
  testId,
  onSelect,
  layout = 'row',
  tone = 'default',
}) {
  const className = cn(
    'flex min-h-[3.25rem] w-full items-center gap-3 rounded-[var(--rely-radius-surface)] border px-3 py-2 text-left',
    'touch-manipulation transition-[background-color,color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    disabled
      ? 'cursor-not-allowed opacity-45'
      : tone === 'primary'
      ? 'border-[var(--rely-color-brand-green)] bg-[var(--rely-surface-selected)] text-[var(--rely-text-strong)] shadow-[var(--rely-shadow-selected)] hover:border-primary-600 dark:hover:border-primary-400'
      : 'border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] text-[var(--rely-text-strong)] hover:border-primary-300 dark:hover:border-primary-800'
  );

  const content = (
    <>
      <span
        data-nav-icon={iconKey}
        data-icon-name={iconName}
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
          tone === 'primary'
            ? 'bg-primary-500/15 text-primary-700 dark:text-primary-300'
            : 'bg-[var(--rely-surface-soft)] text-[var(--rely-text-muted)]'
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={2.35} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1 text-sm font-semibold">{label}</span>
    </>
  );

  if (to && !disabled) {
    return (
      <Link
        to={to}
        onClick={onSelect}
        data-testid={testId || `mobile-sheet-link-${iconKey}`}
        data-density="compact"
        data-layout="row"
        data-tone={tone}
        className={className}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={label}
      data-state={disabled ? 'disabled' : 'enabled'}
      data-testid={testId || `mobile-sheet-link-${iconKey}`}
      data-density="compact"
      data-layout="row"
      data-tone={tone}
      className={className}
    >
      {content}
    </button>
  );
}

function SheetSection({ label, testId, children, columns = false }) {
  return (
    <section
      aria-label={label}
      data-testid={testId}
      data-density="compact"
      data-surface="section-list"
      className="space-y-1.5"
    >
      <p className="px-1 text-[11px] font-semibold leading-none text-[var(--rely-text-muted)]">
        {label}
      </p>
      <div className={cn(columns ? 'grid gap-1' : 'grid gap-1')}>
        {children}
      </div>
    </section>
  );
}

export default function MobileBottomNav() {
  const [showMore, setShowMore] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { logout, user } = useAuth0();
  const { t } = useTranslation();
  const location = useLocation();
  const { selectedCompanyId, hasCompanies } = useCompany();
  const { subscription, hasRecurringSubscription } = useBilling();
  const navigationEntitlements = hasRelyCommandAccess({
    subscription,
    hasRecurringSubscription,
  })
    ? ['invoicing_membership']
    : [];
  const navigationFeatures = isRelyCommandPreviewEnabled()
    ? ['rely_command_preview']
    : [];
  const mobileChrome = getMobileRouteChrome(location.pathname, {
    layoutType: 'empresa',
  });

  React.useEffect(() => {
    if (!showMore && !showLogoutConfirm) return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      setShowMore(false);
      setShowLogoutConfirm(false);
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showMore, showLogoutConfirm]);

  const returnTo =
    window.location.hostname === 'localhost'
      ? 'http://localhost:3000/'
      : 'https://rely.business';

  const closeSheet = () => setShowMore(false);
  const openLogoutConfirm = () => {
    setShowMore(false);
    setShowLogoutConfirm(true);
  };
  const handleLogout = () => {
    setShowLogoutConfirm(false);
    setShowMore(false);
    logout({ logoutParams: { returnTo } });
  };

  const isCompanyProfile = /^\/empresa\/empresas\/[^/]+\/perfil\/?$/.test(
    location.pathname
  );
  const navIcon = (key) => ({
    icon: getRelyNavigationIcon(key),
    iconName: getRelyNavigationIconName(key),
  });

  if (!mobileChrome.showMobileDock) {
    return null;
  }

  const isRouteActive = (route) =>
    Boolean(matchPath({ path: route.path, end: true }, location.pathname));
  const mobileTabRoutes = getClientNavigationItems({
    placement: 'mobile-tab',
    companyId: selectedCompanyId,
    entitlements: navigationEntitlements,
    features: navigationFeatures,
  });
  const mobileSheetRoutes = getClientNavigationItems({
    placement: 'mobile-sheet',
    companyId: selectedCompanyId,
    entitlements: navigationEntitlements,
    features: navigationFeatures,
  });
  const tabs = mobileTabRoutes.map((route) => ({
    key: route.id,
    label: t(
      route.mobileLabelKey || route.labelKey,
      t(route.labelKey, route.fallbackName)
    ),
    ...navIcon(route.iconKey),
    iconKey: route.iconKey,
    to:
      route.id === 'company_dashboard' && route.disabled
        ? ROUTES.HOME
        : route.href,
    active:
      isRouteActive(route) ||
      (route.id === 'company_dashboard' &&
        (isCompanyProfile ||
          location.pathname === ROUTES.HOME ||
          location.pathname === ROUTES.EMPRESAS)),
    disabled: route.id === 'company_dashboard' ? false : route.disabled,
  }));
  const activeSheetRoute = mobileSheetRoutes.find(isRouteActive);
  const moreActive = showMore || Boolean(activeSheetRoute);

  let activeMoreContext = {
    ...navIcon('more'),
    iconKey: 'more',
    label: t('navigation.moreShort', t('navigation.more')),
  };

  if (activeSheetRoute) {
    activeMoreContext = {
      ...navIcon(activeSheetRoute.iconKey),
      iconKey: activeSheetRoute.iconKey,
      label: t(
        activeSheetRoute.mobileLabelKey || activeSheetRoute.labelKey,
        t(activeSheetRoute.labelKey, activeSheetRoute.fallbackName)
      ),
    };
  }

  const sheetRoutes = (...sections) =>
    mobileSheetRoutes.filter((route) => sections.includes(route.section));
  const renderSheetRoute = (route) => (
    <SheetLink
      key={route.id}
      to={route.href}
      {...navIcon(route.iconKey)}
      iconKey={route.navigationKey || route.iconKey}
      testId={
        route.id === 'create_company'
          ? 'mobile-sheet-link-create-company'
          : route.id === 'import_company'
            ? 'mobile-sheet-link-import-company'
            : undefined
      }
      label={t(
        route.sheetLabelKey || route.labelKey,
        t(route.labelKey, route.fallbackName)
      )}
      disabled={route.disabled}
      onSelect={closeSheet}
      tone={route.id === 'create_company' ? 'primary' : 'neutral'}
    />
  );

  return (
    <>
      <nav
        data-testid="mobile-bottom-nav"
        data-rely-mobile-dock="true"
        className="pointer-events-none fixed inset-x-0 z-40 px-3 xl:hidden"
        style={{ bottom: 'var(--rely-mobile-dock-edge)' }}
        aria-label={t('navigation.mainNavigation')}
      >
        <div
          data-testid="mobile-bottom-nav-scrim"
          className="absolute left-1/2 -translate-x-1/2 rounded-[24px] bg-gradient-to-t from-[var(--rely-surface-page)] via-[var(--rely-surface-page)] to-transparent"
          style={{
            bottom: 'calc(var(--rely-mobile-dock-edge) * -1)',
            height: 'var(--rely-mobile-dock-scrim-height)',
            width: 'min(calc(100% - 1rem), 23rem)',
          }}
          aria-hidden="true"
        />
        <div
          data-testid="mobile-bottom-nav-surface"
          data-density="compact"
          data-surface="system-dock"
          className="pointer-events-auto relative mx-auto flex h-[var(--rely-mobile-dock-height)] max-w-[20rem] items-center gap-0.5 rounded-[18px] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-0.5 shadow-[var(--rely-shadow-panel)] backdrop-blur-xl"
        >
          {tabs.map(({ key, ...tab }) => (
            <MobileTab key={key} {...tab} />
          ))}
          <MobileTab
            icon={activeMoreContext.icon}
            iconKey={activeMoreContext.iconKey}
            iconName={activeMoreContext.iconName}
            label={activeMoreContext.label}
            active={moreActive}
            onClick={() => setShowMore(true)}
          />
        </div>
      </nav>

      {showMore && (
        <div
          className="fixed inset-0 z-[70] overscroll-contain bg-neutral-950/45 backdrop-blur-sm xl:hidden"
          role="presentation"
          onClick={closeSheet}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label={t('navigation.mobileActions')}
            data-testid="mobile-more-sheet"
            data-density="compact"
            data-surface="command-sheet"
            className="absolute inset-x-0 bottom-0 max-h-[min(88dvh,42rem)] overflow-y-auto rounded-t-[20px] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-3 text-[var(--rely-text-strong)] shadow-[var(--rely-shadow-panel)]"
            style={{
              paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <div
                data-testid="mobile-sheet-drag-handle"
                className="ml-auto mr-auto h-1 w-10 rounded-full bg-[var(--rely-border-subtle)]"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={closeSheet}
                aria-label={t('close')}
                className="absolute right-3 top-3 flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded-lg bg-[var(--rely-surface-elevated)] text-[var(--rely-text-muted)] transition-colors hover:text-[var(--rely-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            {hasCompanies ? (
              <div
                data-testid="mobile-sheet-company-context"
                data-density="compact"
                data-surface="context-strip"
                className="mb-3 rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] py-2"
              >
                <CompanySelector collapsed={false} />
              </div>
            ) : (
              <div
                data-testid="mobile-sheet-empty-company"
                data-density="compact"
                data-surface="context-strip"
                className="mb-3 rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] p-3"
              >
                <p className="text-sm font-semibold text-[var(--rely-text-strong)]">
                  {t('sidebar.noCompanies')}
                </p>
                <p className="mt-1 text-sm text-[var(--rely-text-muted)]">
                  {t('emptyState.trustIndicator')}
                </p>
              </div>
            )}

            <div className="grid gap-3">
              {!hasCompanies && (
                <SheetSection
                  label={t('navigation.companyActions')}
                  testId="mobile-sheet-company-actions"
                >
                  {sheetRoutes('company_actions').map(renderSheetRoute)}
                </SheetSection>
              )}

              <SheetSection
                label={t('navigation.finance')}
                testId="mobile-sheet-finance"
              >
                {sheetRoutes('finance').map(renderSheetRoute)}
              </SheetSection>

              <SheetSection
                label={t('navigation.resources')}
                testId="mobile-sheet-resources"
              >
                {sheetRoutes('resources').map(renderSheetRoute)}
              </SheetSection>

              <SheetSection
                label={t('navigation.accountActions')}
                testId="mobile-sheet-account-actions"
              >
                {sheetRoutes('account').map(renderSheetRoute)}
              </SheetSection>
            </div>

            {user && (
              <div
                data-testid="mobile-sheet-user-context"
                data-density="compact"
                data-surface="account-strip"
                className="mt-3 rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] p-2.5"
              >
                <div className="flex items-center gap-3">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || user.email}
                      width="44"
                      height="44"
                      className="h-9 w-9 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--rely-text-strong)] text-[var(--rely-surface-panel)] dark:bg-primary-500 dark:text-neutral-950">
                      <span className="text-sm font-bold">
                        {getInitial(user.name || user.email)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[var(--rely-text-strong)]">
                      {user.name || user.email?.split('@')[0] || t('user')}
                    </p>
                    <p className="truncate text-xs text-[var(--rely-text-muted)]">
                      {user.email}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openLogoutConfirm}
                    className="flex min-h-[44px] min-w-[44px] touch-manipulation items-center justify-center rounded-lg text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:text-red-400 dark:hover:bg-red-900/20"
                    aria-label={t('sidebar.logout')}
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {showLogoutConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-logout-dialog-title"
          aria-describedby="mobile-logout-dialog-description"
          className="fixed inset-0 z-[80] flex items-center justify-center overscroll-contain bg-neutral-950/55 p-4 backdrop-blur-sm"
        >
          <div
            data-testid="mobile-logout-dialog-panel"
            data-surface="logout-dialog"
            className="w-full max-w-sm rounded-[var(--rely-radius-panel)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-panel)] p-5 text-[var(--rely-text-strong)] shadow-[var(--rely-shadow-panel)]"
          >
            <div
              data-testid="mobile-logout-dialog-icon"
              data-tone="neutral"
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-[var(--rely-radius-surface)] border border-[var(--rely-border-subtle)] bg-[var(--rely-surface-elevated)] text-[var(--rely-text-muted)]"
            >
              <LogOut className="h-5 w-5" aria-hidden="true" />
            </div>
            <h3
              id="mobile-logout-dialog-title"
              className="mb-2 text-lg font-semibold text-[var(--rely-text-strong)]"
            >
              {t('sidebar.logoutConfirmTitle')}
            </h3>
            <p
              id="mobile-logout-dialog-description"
              className="mb-6 text-sm leading-6 text-[var(--rely-text-muted)]"
            >
              {t('sidebar.logoutConfirmMessage')}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="default"
                className="flex-1 border border-error-500/40 bg-transparent text-error-600 hover:bg-error-500/10 dark:text-error-300"
                onClick={handleLogout}
              >
                {t('sidebar.logout')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
